import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Command } from 'cmdk';
import { Dialog } from 'radix-ui';
import { AnimatePresence, m } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, FileText, Hammer, Search, User } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { partiesQuery } from '@/features/customers/api';
import { quotesQuery } from '@/features/quotes/api';
import { worksQuery } from '@/features/works/api';
import { useDebounced } from '@/lib/useDebounced';
import { Kbd } from '@/design/components/Display';
import { useVisibleNav } from './Sidebar';

export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return { open, setOpen };
}

const itemCls =
  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-ink-700 outline-none data-[selected=true]:bg-gold-50 data-[selected=true]:text-ink-900';

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { sections, actions } = useVisibleNav();
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), 250);
  const customers = useQuery({ ...partiesQuery('CUSTOMER', q, 6), enabled: open && q.length >= 2 && can('clients.read') });
  const quotes = useQuery({ ...quotesQuery({ q }, 6), enabled: open && q.length >= 2 && can('quotes.read') });
  const works = useQuery({ ...worksQuery({ q }, 6), enabled: open && q.length >= 2 && can('works.read') });

  const go = (to: string) => {
    onOpenChange(false);
    setQuery('');
    navigate(to);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <m.div className="fixed inset-0 z-[80] bg-ink-900/20 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
            </Dialog.Overlay>
            <Dialog.Content asChild forceMount>
              <m.div
                className="fixed inset-x-3 top-[12vh] z-[80] mx-auto max-w-xl overflow-hidden rounded-2xl border border-line bg-surface-0 shadow-e4"
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -6, scale: 0.98, transition: { duration: 0.12 } }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              >
                <Dialog.Title className="sr-only">Buscar e ir</Dialog.Title>
                <Dialog.Description className="sr-only">Buscá pantallas, clientes y presupuestos</Dialog.Description>
                <Command loop shouldFilter={true}>
                  <div className="flex items-center gap-3 border-b border-line px-4">
                    <Search className="size-4 text-ink-400" />
                    <Command.Input
                      value={query}
                      onValueChange={setQuery}
                      placeholder="Buscá un cliente, un presupuesto o una pantalla…"
                      className="h-14 w-full bg-transparent text-[15px] outline-none placeholder:text-ink-300"
                    />
                    <Kbd>Esc</Kbd>
                  </div>
                  <Command.List className="scrollbar-thin max-h-[60vh] overflow-y-auto p-2">
                    <Command.Empty className="py-8 text-center text-sm text-ink-500">No encontramos nada con eso.</Command.Empty>
                    {actions.length > 0 && (
                      <Command.Group heading="Acciones" className="[&_[cmdk-group-heading]]:overline [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
                        {actions.map((a) => (
                          <Command.Item key={a.to} value={a.label} onSelect={() => go(a.to)} className={itemCls}>
                            <span className="gold-gradient flex size-7 items-center justify-center rounded-lg text-ink-900">
                              <a.icon className="size-4" />
                            </span>
                            {a.label}
                          </Command.Item>
                        ))}
                      </Command.Group>
                    )}
                    {(customers.data?.length ?? 0) > 0 && (
                      <Command.Group heading="Clientes" className="[&_[cmdk-group-heading]]:overline [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
                        {customers.data!.map((c) => (
                          <Command.Item key={c.id} value={`cliente ${c.name} ${c.phone ?? ''} ${query}`} onSelect={() => go(`/clientes/${c.id}`)} className={itemCls}>
                            <User className="size-4 text-ink-400" />
                            <span className="flex-1 truncate">{c.name}</span>
                            <span className="text-[12px] text-ink-400">{c.phone}</span>
                          </Command.Item>
                        ))}
                      </Command.Group>
                    )}
                    {(quotes.data?.length ?? 0) > 0 && (
                      <Command.Group heading="Presupuestos" className="[&_[cmdk-group-heading]]:overline [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
                        {quotes.data!.map((qt) => (
                          <Command.Item key={qt.id} value={`presupuesto ${qt.number} ${qt.customerName ?? ''} ${query}`} onSelect={() => go(`/presupuestos/${qt.id}`)} className={itemCls}>
                            <FileText className="size-4 text-ink-400" />
                            <span className="tabular font-medium">{qt.number}</span>
                            <span className="flex-1 truncate text-ink-500">{qt.customerName}</span>
                          </Command.Item>
                        ))}
                      </Command.Group>
                    )}
                    {(works.data?.length ?? 0) > 0 && (
                      <Command.Group heading="Trabajos" className="[&_[cmdk-group-heading]]:overline [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
                        {works.data!.map((w) => (
                          <Command.Item key={w.id} value={`trabajo ${w.number} ${w.customerName ?? ''} ${query}`} onSelect={() => go(`/trabajos/${w.id}`)} className={itemCls}>
                            <Hammer className="size-4 text-ink-400" />
                            <span className="tabular font-medium">{w.number}</span>
                            <span className="flex-1 truncate text-ink-500">{w.customerName}</span>
                          </Command.Item>
                        ))}
                      </Command.Group>
                    )}
                    <Command.Group heading="Ir a" className="[&_[cmdk-group-heading]]:overline [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5">
                      {sections.flatMap((s) => s.items).map((i) => (
                        <Command.Item key={i.to} value={`ir ${i.label}`} onSelect={() => go(i.to)} className={itemCls}>
                          <i.icon className="size-4 text-ink-400" strokeWidth={1.75} />
                          <span className="flex-1">{i.label}</span>
                          <ArrowRight className="size-3.5 text-ink-300" />
                        </Command.Item>
                      ))}
                    </Command.Group>
                  </Command.List>
                </Command>
              </m.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
